import React, { ReactNode, ChangeEvent, FocusEvent } from 'react';
import { Input } from '../Input';
import classnames from 'classnames';

import { SIZES, DIRECTIONS } from '../Common/constants';

import styles from './main.scss';

type NumberInputRequiredProps = {
    dataTn: string;
};

type NumberInputOptionalProps = {
    alignRight?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    errorDataTn?: string;
    errorMessage?: ReactNode | { __html: string };
    errorMessageAlignment?: typeof DIRECTIONS.right | typeof DIRECTIONS.left;
    hasAnimatedPlaceholder?: boolean;
    hasError?: boolean;
    horizontalSpacing?: typeof SIZES.def | typeof SIZES.small;
    label?: ReactNode;
    leftDecorator?: ReactNode;
    locale?: string;
    name?: string;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    rightDecorator?: ReactNode;
    size?: typeof SIZES.small | typeof SIZES.medium | typeof SIZES.large;
    value?: string;
};

type NumberInputProps = NumberInputRequiredProps & NumberInputOptionalProps;

type NumberInputState = {
    value: string;
    cursorIndex: number;
};

export class NumberInput extends React.Component<NumberInputProps, NumberInputState> {
    static defaultProps = {
        autoFocus: false,
        disabled: false,
        errorMessageAlignment: DIRECTIONS.left,
        hasError: false,
        horizontalSpacing: SIZES.def,
    };

    constructor(props: NumberInputProps) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.inputNode = null;

        this.state = {
            value: this.normalizeInput(props.value || ''),
            cursorIndex: 0,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: NumberInputProps): void {
        const newValue = nextProps.value || '';
        const normalizeValue = this.normalizeInput(newValue);
        if (this.state.value !== normalizeValue) {
            this.setState({ value: normalizeValue });
        }
    }

    componentDidUpdate(): void {
        const node = this.inputNode;

        /* istanbul ignore next - mount doesn't handle refs */
        if (node && document && document.activeElement === node) {
            const { number: formattedNumber } = this.formatInput(this.state.value);
            node.value = formattedNumber === '0' && !node.value ? '' : formattedNumber;
            node.selectionStart = node.selectionEnd = this.state.cursorIndex;
        }
    }

    inputNode: HTMLInputElement | null;

    normalizeInput = (incomingValue: string): string => {
        const regexValue = String(incomingValue).match(/^\d*\.?\d*/);
        const value = regexValue ? regexValue[0] : '';

        return value ? parseFloat(value).toFixed(0) : '';
    };

    formatInput = (value: string): { number: string } => {
        const { locale } = this.props;

        const number = new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
            // @ts-ignore - string type should be number, requires a decent amount of refactoring
        }).format(value);

        return {
            number,
        };
    };

    getRawNumbersAsString = (str: string): string => {
        const parsedString = str.replace(/[^\d]/g, '');
        if (parsedString) {
            return parseInt(parsedString).toString();
        }
        return parsedString;
    };

    handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
        const { onBlur } = this.props;
        if (onBlur) {
            e.target.value = this.getRawNumbersAsString(e.target.value);
            onBlur(e);
        }
    };

    handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
        // on focus, select the entire given input.
        if (!this.inputNode) {
            this.inputNode = e.target;
        }

        const node = this.inputNode;

        if (node && document && document.activeElement === node) {
            node.selectionStart = 0;
            node.selectionEnd = node.value.length;
        }
    };

    handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        this.inputNode = e.target;

        const { number: formattedStateValue } = this.formatInput(this.state.value);

        let newValue = e.target.value;
        let index = e.target.selectionStart;

        // Handle backspace on "," or "."
        if (newValue.length === formattedStateValue.length - 1) {
            if (typeof index === 'number' && !(parseInt(formattedStateValue[index]) >= 0)) {
                newValue = newValue.slice(0, index - 1) + newValue.slice(index);
                index = index - 2;
                if (index < 0) {
                    index = 0;
                }
            }
        }

        const rawValue = this.getRawNumbersAsString(newValue);

        if (this.props.onChange) {
            e.target.value = rawValue;
            this.props.onChange(e);
        }

        if (typeof index === 'number' && formattedStateValue !== newValue) {
            const { number: formattedInputValue } = this.formatInput(rawValue);
            const distanceFromEnd = newValue.length - index;
            let nextIndex = formattedInputValue.length - distanceFromEnd;

            if (nextIndex < 0) {
                nextIndex = 0;
            }

            this.setState({
                value: rawValue,
                cursorIndex: nextIndex,
            });
        }
    };

    render(): ReactNode {
        const {
            alignRight,
            autoFocus,
            dataTn,
            disabled,
            errorDataTn,
            errorMessage,
            errorMessageAlignment,
            hasAnimatedPlaceholder,
            hasError,
            horizontalSpacing,
            label,
            leftDecorator,
            name,
            placeholder,
            rightDecorator,
            size,
        } = this.props;
        const { value } = this.state;
        const { number: formattedValue } = this.formatInput(value || '');

        let displayVal = '';
        if (parseInt(value) >= 0) {
            displayVal = formattedValue;
        }

        const inputClassNames = classnames({
            [styles.alignRight]: alignRight,
        });

        const inputErrorDataTn = errorDataTn ? errorDataTn : `${dataTn}-error`;

        return (
            <Input
                autoFocus={autoFocus}
                className={inputClassNames}
                dataTn={dataTn}
                disabled={disabled}
                errorDataTn={inputErrorDataTn}
                errorMessage={errorMessage}
                errorMessageAlignment={errorMessageAlignment}
                hasAnimatedPlaceholder={hasAnimatedPlaceholder}
                hasError={hasError}
                horizontalSpacing={horizontalSpacing}
                label={label}
                leftDecorator={leftDecorator}
                maxLength={17}
                name={name}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                placeholder={placeholder}
                rightDecorator={rightDecorator}
                showIcons={false}
                size={size}
                value={displayVal}
            />
        );
    }
}
